import React, { useState, useRef, useEffect } from 'react';
import {
  DatePicker,
  Space,
  Collapse,
  Checkbox,
  Table,
  Radio,
  Button
} from 'antd';
import { useParams } from 'react-router-dom';
import {
  DemographicsContainer,
  DemographicsTitleDiv,
  DemographicsContentDiv,
  SourceDiv,
  ItemsContainer,
  CodeButton
} from './styledComponents';

import CustomInput from '../../CustomComponents/CustomInput';
import InferenceEditor from './InferenceEditor';
import { Empty, Spin, Tooltip, notification } from 'antd';
import { StyledAsterisk } from './InferenceEditor/styledComponents';
import { updateDate, updatePatientDataSelection } from '../../../Apis/Jobs';
import {
  formatDate,
  formatTime,
  formatDateTime
} from '../../../Utils/dateFormat';

const Demographics = ({
  theme,
  content,
  cptCodesLoader,
  inference,
  setHighlightWord,
  setinitIgnore,
  setIgnore,
  matchedCodes,
  inferenceAnnotation,
  editorData,
  activeSection,
  emptySection,
  updateInferenceLoading,
  setUpdateInferenceLoading,
  viewOnly,
  printInfoData,
  patientData,
  patientDataSelection,
  onSelectionSaved
}) => {
  const [searchClicked, setSearchClicked] = useState(false);
  const [previewData, setPreviewData] = useState('');
  const [demographicsContents, setDemographicsContents] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const { jobId } = useParams();
  const [activeBillingKey, setActiveBillingKey] = useState([]);
  const [vitalsData, setVitalsData] = useState([]);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [allergiesData, setAllergiesData] = useState([]);
  const [allergiesLoading, setAllergiesLoading] = useState(false);
  const [medDiagnosisData, setMedDiagnosisData] = useState([]);
  const [labReportsData, setLabReportsData] = useState([]);
  const [labReportsLoading, setLabReportsLoading] = useState(false);
  const [medicationsData, setMedicationsData] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [progressNoteData, setProgressNoteData] = useState([]);
  const [progressNoteLoading, setProgressNoteLoading] = useState(false);
  const [expandedProgressNotes, setExpandedProgressNotes] = useState([]);
  const [selectedKeyPhrases, setSelectedKeyPhrases] = useState({
    allergies: [],
    medDiagnosis: [],
    labReports: [],
    medications: [],
    progressNote: null
  });
  const [dataSelectionLoaded, setDataSelectionLoaded] = useState(false);
  const isInitialSelectionRef = useRef(true);
  const demoContainerRef = useRef(null);

  // Scroll fade gradient: detect overflow and toggle classes on .ant-collapse-content-box
  useEffect(() => {
    const container = demoContainerRef.current;
    if (!container) return;

    const checkOverflow = el => {
      const hasOverflow = el.scrollHeight > el.clientHeight;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
      if (hasOverflow && !atBottom) {
        el.classList.add('scroll-fade-active');
        el.classList.remove('scroll-fade-bottom');
      } else if (hasOverflow && atBottom) {
        el.classList.add('scroll-fade-active');
        el.classList.add('scroll-fade-bottom');
      } else {
        el.classList.remove('scroll-fade-active');
        el.classList.remove('scroll-fade-bottom');
      }
    };

    const handleScroll = e => checkOverflow(e.target);

    const setupListeners = () => {
      const boxes = container.querySelectorAll('.ant-collapse-content-box');
      boxes.forEach(el => {
        checkOverflow(el);
        el.addEventListener('scroll', handleScroll);
      });
      return boxes;
    };

    // Initial setup with a small delay to let Ant Design render panels
    const timeoutId = setTimeout(() => setupListeners(), 300);

    // Re-check when panels expand/collapse via MutationObserver
    const observer = new MutationObserver(() => {
      const boxes = container.querySelectorAll('.ant-collapse-content-box');
      boxes.forEach(el => {
        checkOverflow(el);
        el.removeEventListener('scroll', handleScroll);
        el.addEventListener('scroll', handleScroll);
      });
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      const boxes = container.querySelectorAll('.ant-collapse-content-box');
      boxes.forEach(el => el.removeEventListener('scroll', handleScroll));
    };
  }, [
    vitalsData,
    allergiesData,
    medDiagnosisData,
    labReportsData,
    medicationsData,
    progressNoteData
  ]);

  useEffect(() => {
    setDemographicsContents(content);
    setEditKey(false);
  }, [content]);

  useEffect(() => {
    editorData.forEach(item => {
      if (activeSection === item.id) {
        setPreviewData(item.content && item.content[0].value);
      }
    });
  });

  // Process patient data selection from parent (centralized fetch)
  useEffect(() => {
    if (dataSelectionLoaded) return;
    if (!patientDataSelection) return;

    // Wait for at least some data to be loaded first
    if (
      allergiesData.length === 0 &&
      medDiagnosisData.length === 0 &&
      medicationsData.length === 0 &&
      progressNoteData.length === 0
    ) {
      return;
    }

    const selectionData = patientDataSelection;

    const newSelections = {
      allergies: [],
      medDiagnosis: [],
      labReports: [],
      medications: [],
      progressNote: null
    };

    // Pre-select allergies
    if (selectionData.allergies && selectionData.allergies.length > 0) {
      newSelections.allergies = selectionData.allergies
        .filter(item => item.selected)
        .map(item => item.allergyIntoleranceData?.allergen)
        .filter(Boolean);
    }

    // Pre-select med diagnosis
    if (selectionData.conditions && selectionData.conditions.length > 0) {
      newSelections.medDiagnosis = selectionData.conditions
        .filter(item => item.selected)
        .map(
          item =>
            item.condition?.icd10Description ||
            item.condition?.conditionCode?.codings?.[0]?.display
        )
        .filter(Boolean);
    }

    // Pre-select lab reports
    if (selectionData.labReports && selectionData.labReports.length > 0) {
      newSelections.labReports = selectionData.labReports
        .filter(item => item.selected)
        .map(item => item.labReport?.reportId)
        .filter(Boolean);
    }

    // Pre-select medications
    if (selectionData.medications && selectionData.medications.length > 0) {
      newSelections.medications = selectionData.medications
        .filter(item => item.selected)
        .map(item => item.medication?.orderId)
        .filter(Boolean);
    }

    // Pre-select progress note
    if (selectionData.progressNotes && selectionData.progressNotes.length > 0) {
      const selectedNote = selectionData.progressNotes.find(
        item => item.selected
      );
      if (selectedNote) {
        newSelections.progressNote = selectedNote.notes?.progressNoteId;
      }
    }

    setSelectedKeyPhrases(newSelections);
    setDataSelectionLoaded(true);
  }, [
    patientDataSelection,
    allergiesData,
    medDiagnosisData,
    medicationsData,
    progressNoteData,
    dataSelectionLoaded
  ]);

  useEffect(() => {
    if (searchClicked === false) {
      setDemographicsContents(content);
    }
    // eslint-disable-next-line
  }, [searchClicked]);

  // Function to persist checkbox selections to API
  const persistDataSelection = () => {
    if (!jobId || !dataSelectionLoaded) {
      return;
    }

    const payload = {
      allergies: (allergiesData || []).map(allergy => ({
        allergyIntoleranceData: {
          allergen: allergy.allergen,
          allergyIntoleranceId: allergy.allergyIntoleranceId,
          category: allergy.category,
          severity: allergy.severity,
          type: allergy.type
        },
        selected: (selectedKeyPhrases.allergies || []).includes(
          allergy.allergen
        )
      })),
      conditions: (medDiagnosisData || []).map(diagnosis => ({
        condition: {
          conditionCode: {
            codings: [
              {
                code: diagnosis.code,
                display: diagnosis.display,
                system: diagnosis.system,
                version: diagnosis.version
              }
            ]
          },
          icd10: diagnosis.icd10,
          icd10Description: diagnosis.icd10Description,
          clinicalStatus: diagnosis.clinicalStatus
        },
        selected: (selectedKeyPhrases.medDiagnosis || []).includes(
          diagnosis.display
        )
      })),
      labReports: (labReportsData || []).map(report => ({
        labReport: {
          reportId: report.reportId,
          performingLaboratory: report.performingLaboratory,
          reportingLaboratory: report.reportingLaboratory,
          effectiveDateTime: report.effectiveDateTime,
          issuedDateTime: report.issuedDateTime,
          testSet: report.testSet
        },
        selected: (selectedKeyPhrases.labReports || []).includes(
          report.reportId
        )
      })),
      medications: (medicationsData || []).map(med => ({
        medication: {
          orderId: med.orderId,
          description: med.description,
          strength: med.strength,
          strengthUOM: med.strengthUOM,
          directions: med.directions,
          startDate: med.startDate,
          schedules: med.schedules
        },
        selected: (selectedKeyPhrases.medications || []).includes(med.orderId)
      })),
      observations: (vitalsData || []).map(vital => ({
        type: vital.type || '',
        value: vital.value || '',
        unit: vital.unit || '',
        recordedDate: vital.recordedDate || '',
        recordedAt: vital.recordedAt || '',
        method: '',
        observationId: 0,
        patientId: 0,
        recordedBy: '',
        deviceId: '',
        deviceName: '',
        diastolicValue: 0,
        systolicValue: 0,
        latestVitals: true,
        selected: true // Vitals don't have checkboxes, so all are selected
      })),
      progressNotes: (progressNoteData || []).map(note => ({
        notes: {
          progressNoteId: note.progressNoteId,
          progressNoteType: note.progressNoteType,
          createdBy: note.createdBy,
          createdDate: note.createdDate,
          effectiveDate: note.effectiveDate,
          jobForProgressNote: note.jobForProgressNote
        },
        selected: selectedKeyPhrases.progressNote === note.progressNoteId
      }))
    };

    updatePatientDataSelection(jobId, payload)
      .then(response => {
        notification.success({
          key: 'patient-data-selection',
          message: 'Selection Saved',
          description: 'Patient data selection has been saved successfully',
          duration: 2
        });
        // Pass POST response data to parent - no need for separate GET since response is same
        if (onSelectionSaved) {
          onSelectionSaved(response.data?.result?.[0] || null);
        }
      })
      .catch(err => {
        console.error('❌ Error updating patient data selection:', err);
        if (err.response) {
          console.error('Error response:', err.response.data);
          console.error('Error status:', err.response.status);
        }
        notification.error({
          key: 'patient-data-selection',
          message: 'Save Failed',
          description:
            err.response?.data?.message ||
            'Failed to save patient data selection',
          duration: 3
        });
      });
  };

  // Auto-persist on every checkbox/radio selection change
  useEffect(() => {
    if (!dataSelectionLoaded) return;
    // Skip the initial selection restore from saved data
    if (isInitialSelectionRef.current) {
      isInitialSelectionRef.current = false;
      return;
    }
    persistDataSelection();
    // eslint-disable-next-line
  }, [selectedKeyPhrases]);

  // Sync patient data from parent (centralized fetch)
  useEffect(() => {
    if (!patientData || !patientData.loaded) return;

    // Process vitals for display
    if (patientData.vitals?.length > 0) {
      const observations = patientData.vitals.map((obs, index) => ({
        key: index.toString(),
        type: obs.type || 'N/A',
        value: `${obs.value || 'N/A'} ${obs.unit || ''}`.trim(),
        recordedDate: obs.recordedDate
          ? formatDate(obs.recordedDate)
          : 'N/A',
        recordedAt: obs.recordedAt
          ? formatTime(obs.recordedAt)
          : 'N/A'
      }));
      setVitalsData(observations);
    }
    setVitalsLoading(false);

    // Process conditions for display (already in correct format from parent)
    if (patientData.conditions?.length > 0) {
      setMedDiagnosisData(patientData.conditions);
    }

    // Process allergies for display (already in correct format from parent)
    if (patientData.allergies?.length > 0) {
      setAllergiesData(patientData.allergies);
    }
    setAllergiesLoading(false);

    // Process lab reports for display (already in correct format from parent)
    if (patientData.labReports?.length > 0) {
      setLabReportsData(patientData.labReports);
    }
    setLabReportsLoading(false);

    // Process medications for display (already in correct format from parent)
    if (patientData.medications?.length > 0) {
      setMedicationsData(patientData.medications);
    }
    setMedicationsLoading(false);

    // Process progress notes for display (already in correct format from parent)
    if (patientData.progressNotes?.length > 0) {
      setProgressNoteData(patientData.progressNotes);
    }
    setProgressNoteLoading(false);
  }, [patientData]);

  return (
    <DemographicsContainer
      ref={demoContainerRef}
      style={{ height: '100%', overflowY: 'auto' }}
    >
      <Collapse
        activeKey={activeBillingKey}
        onChange={keys => setActiveBillingKey(keys)}
        bordered={false}
        expandIconPosition='end'
        style={{ gap: 0 }}
      >
        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Billing
            </DemographicsTitleDiv>
          }
          key='billing'
          showArrow={true}
        >
          <DemographicsContentDiv>
            <SourceDiv>Code found in text</SourceDiv>
            <ItemsContainer>
              {matchedCodes.length > 0 ? (
                matchedCodes.map((code, index) => (
                  <div
                    key={index}
                    className='cpt-code-item'
                    onMouseEnter={() => setHighlightWord(code.cptCode)}
                  >
                    {code.cptCode}
                  </div>
                ))
              ) : (
                <p
                  style={{
                    marginLeft: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#6b7280',
                    padding: '6px 0',
                    fontStyle: 'italic'
                  }}
                >
                  No matching CPT codes
                </p>
              )}
            </ItemsContainer>

          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Vitals
            </DemographicsTitleDiv>
          }
          key='vitals'
          showArrow={true}
        >
          <DemographicsContentDiv>
            <Table
              dataSource={vitalsData}
              loading={vitalsLoading}
              columns={[
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  render: (text, record) => (
                    <div>
                      <div style={{ color: '#1890ff', fontWeight: '500' }}>
                        {text}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '4px'
                        }}
                      >
                        {record.value}
                      </div>
                    </div>
                  )
                },
                {
                  title: 'Recorded Date',
                  dataIndex: 'recordedDate',
                  key: 'recordedDate'
                },
                {
                  title: 'Recorded At',
                  dataIndex: 'recordedAt',
                  key: 'recordedAt'
                }
              ]}
              pagination={false}
              size='small'
            />
          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Allergies
            </DemographicsTitleDiv>
          }
          key='allergies'
          showArrow={true}
        >
          <DemographicsContentDiv style={{ marginTop: '10px' }}>
            {allergiesLoading ? (
              <Spin />
            ) : allergiesData.length > 0 ? (
              allergiesData.map((allergy, index) => (
                <div key={index} style={{ marginBottom: '10px', marginLeft: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: '500' }}>
                      {allergy.allergen}
                    </span>
                    {(allergy.severity || allergy.type) && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '2px'
                        }}
                      >
                        {[allergy.severity, allergy.type]
                          .filter(Boolean)
                          .join(' - ')}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p
                style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}
              >
                No allergies available
              </p>
            )}
          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Med Diagnosis
            </DemographicsTitleDiv>
          }
          key='medDiagnosis'
          showArrow={true}
        >
          <DemographicsContentDiv
            style={{
              marginTop: '10px'
            }}
          >
            {vitalsLoading ? (
              <Spin />
            ) : medDiagnosisData.length > 0 ? (
              <>
                <div style={{ marginBottom: '8px' }}>
                  <Checkbox
                    checked={medDiagnosisData.every(d =>
                      selectedKeyPhrases.medDiagnosis.includes(d.display)
                    )}
                    indeterminate={
                      selectedKeyPhrases.medDiagnosis.length > 0 &&
                      !medDiagnosisData.every(d =>
                        selectedKeyPhrases.medDiagnosis.includes(d.display)
                      )
                    }
                    onChange={() => {
                      const allSelected = medDiagnosisData.every(d =>
                        selectedKeyPhrases.medDiagnosis.includes(d.display)
                      );
                      setSelectedKeyPhrases(prev => ({
                        ...prev,
                        medDiagnosis: allSelected
                          ? []
                          : medDiagnosisData.map(d => d.display)
                      }));
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#1890ff' }}>Select All</span>
                  </Checkbox>
                </div>
                {medDiagnosisData.map((diagnosis, index) => (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <Checkbox
                    checked={selectedKeyPhrases.medDiagnosis.includes(
                      diagnosis.display
                    )}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          medDiagnosis: [
                            ...prev.medDiagnosis,
                            diagnosis.display
                          ]
                        }));
                      } else {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          medDiagnosis: prev.medDiagnosis.filter(
                            p => p !== diagnosis.display
                          )
                        }));
                      }
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: '500' }}>
                        {diagnosis.display}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '2px'
                        }}
                      >
                        {diagnosis.icd10 && `ICD-10: ${diagnosis.icd10}`}
                        {diagnosis.icd10 && diagnosis.code && ' | '}
                        {diagnosis.code && `SNOMED: ${diagnosis.code}`}
                      </span>
                    </div>
                  </Checkbox>
                </div>
              ))}
              </>
            ) : (
              <p
                style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}
              >
                No medical diagnosis available
              </p>
            )}
          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Lab Reports
            </DemographicsTitleDiv>
          }
          key='labReports'
          showArrow={true}
        >
          <DemographicsContentDiv style={{ marginTop: '10px' }}>
            {labReportsLoading ? (
              <Spin />
            ) : labReportsData.length > 0 ? (
              <>
                <div style={{ marginBottom: '8px' }}>
                  <Checkbox
                    checked={labReportsData.every(r =>
                      selectedKeyPhrases.labReports.includes(r.reportId)
                    )}
                    indeterminate={
                      selectedKeyPhrases.labReports.length > 0 &&
                      !labReportsData.every(r =>
                        selectedKeyPhrases.labReports.includes(r.reportId)
                      )
                    }
                    onChange={() => {
                      const allSelected = labReportsData.every(r =>
                        selectedKeyPhrases.labReports.includes(r.reportId)
                      );
                      setSelectedKeyPhrases(prev => ({
                        ...prev,
                        labReports: allSelected
                          ? []
                          : labReportsData.map(r => r.reportId)
                      }));
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#1890ff' }}>Select All</span>
                  </Checkbox>
                </div>
                {labReportsData.map((report, index) => (
                <div key={index}>
                  <Checkbox
                    checked={selectedKeyPhrases.labReports.includes(
                      report.reportId
                    )}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          labReports: [...prev.labReports, report.reportId]
                        }));
                      } else {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          labReports: prev.labReports.filter(
                            p => p !== report.reportId
                          )
                        }));
                      }
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: '500' }}>
                        {report.performingLaboratory}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '2px'
                        }}
                      >
                        Effective:{' '}
                        {report.effectiveDateTime
                          ? formatDateTime(report.effectiveDateTime)
                          : 'N/A'}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '2px'
                        }}
                      >
                        Issued:{' '}
                        {report.issuedDateTime
                          ? formatDateTime(report.issuedDateTime)
                          : 'N/A'}
                      </span>
                    </div>
                  </Checkbox>
                </div>
              ))}
              </>
            ) : (
              <p
                style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}
              >
                No lab reports available
              </p>
            )}
          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Medications
            </DemographicsTitleDiv>
          }
          key='medications'
          showArrow={true}
        >
          <DemographicsContentDiv style={{ marginTop: '10px' }}>
            {medicationsLoading ? (
              <Spin />
            ) : medicationsData.length > 0 ? (
              <>
                <div style={{ marginBottom: '8px' }}>
                  <Checkbox
                    checked={medicationsData.every(m =>
                      selectedKeyPhrases.medications.includes(m.orderId)
                    )}
                    indeterminate={
                      selectedKeyPhrases.medications.length > 0 &&
                      !medicationsData.every(m =>
                        selectedKeyPhrases.medications.includes(m.orderId)
                      )
                    }
                    onChange={() => {
                      const allSelected = medicationsData.every(m =>
                        selectedKeyPhrases.medications.includes(m.orderId)
                      );
                      setSelectedKeyPhrases(prev => ({
                        ...prev,
                        medications: allSelected
                          ? []
                          : medicationsData.map(m => m.orderId)
                      }));
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#1890ff' }}>Select All</span>
                  </Checkbox>
                </div>
                {medicationsData.map((medication, index) => (
                <div key={index}>
                  <Checkbox
                    checked={selectedKeyPhrases.medications.includes(
                      medication.orderId
                    )}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          medications: [...prev.medications, medication.orderId]
                        }));
                      } else {
                        setSelectedKeyPhrases(prev => ({
                          ...prev,
                          medications: prev.medications.filter(
                            p => p !== medication.orderId
                          )
                        }));
                      }
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: '500' }}>
                        {medication.description}
                      </span>
                      {medication.strength && medication.strength !== 'N/A' && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '2px'
                          }}
                        >
                          Strength: {medication.strength}{' '}
                          {medication.strengthUOM}
                        </span>
                      )}
                      {medication.startDate && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '2px'
                          }}
                        >
                          Start Date:{' '}
                          {formatDate(medication.startDate)}
                        </span>
                      )}
                    </div>
                  </Checkbox>
                </div>
              ))}
              </>
            ) : (
              <p
                style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}
              >
                No medications available
              </p>
            )}
          </DemographicsContentDiv>
        </Collapse.Panel>

        <Collapse.Panel
          header={
            <DemographicsTitleDiv background={theme && theme['@text-color']}>
              Progress Note
            </DemographicsTitleDiv>
          }
          key='progressNote'
          showArrow={true}
        >
          <DemographicsContentDiv style={{ paddingTop: '12px' }}>
            {progressNoteLoading ? (
              <Spin />
            ) : progressNoteData.length > 0 ? (
              progressNoteData.map((note, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom:
                      index === progressNoteData.length - 1 ? '20px' : '12px',
                    paddingBottom:
                      index === progressNoteData.length - 1 ? '12px' : '0px',
                    borderBottom:
                      index === progressNoteData.length - 1
                        ? '1px solid #e8e8e8'
                        : 'none'
                  }}
                >
                  <Collapse
                    bordered={false}
                    expandIconPosition='end'
                    activeKey={expandedProgressNotes}
                    onChange={keys => setExpandedProgressNotes(keys)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px'
                    }}
                  >
                    <Collapse.Panel
                      header={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {note.jobId && (
                            <Radio
                              checked={
                                selectedKeyPhrases.progressNote ===
                                note.progressNoteId
                              }
                              onChange={e => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setSelectedKeyPhrases(prev => ({
                                    ...prev,
                                    progressNote: note.progressNoteId
                                  }));
                                }
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              marginLeft: '8px'
                            }}
                          >
                            <span
                              style={{ fontWeight: '500', color: '#1890ff' }}
                            >
                              {note.createdBy || 'Unknown'}
                            </span>
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#666',
                                marginTop: '2px'
                              }}
                            >
                              {note.progressNoteType || 'Progress Note'}
                            </span>
                            {note.createdDate && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  color: '#999',
                                  marginTop: '2px'
                                }}
                              >
                                {formatDateTime(note.createdDate)}
                              </span>
                            )}
                          </div>
                        </div>
                      }
                      key={`note-${note.progressNoteId}`}
                    >
                      <div style={{ padding: '8px 0' }}>
                        {note.sectionObjects &&
                        note.sectionObjects.length > 0 ? (
                          note.sectionObjects.map((section, sectionIndex) => (
                            <div
                              key={sectionIndex}
                              style={{ marginBottom: '16px' }}
                            >
                              <h4
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#262626',
                                  marginBottom: '8px'
                                }}
                              >
                                {section.heading?.customUIHeading || 'Section'}
                              </h4>
                              {section.content &&
                                section.content.length > 0 && (
                                  <div>
                                    {section.content.map(
                                      (contentItem, contentIndex) => (
                                        <div
                                          key={contentIndex}
                                          style={{
                                            fontSize: '13px',
                                            color: '#595959',
                                            lineHeight: '1.6',
                                            marginBottom: '8px'
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: contentItem.value || ''
                                          }}
                                        />
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                          ))
                        ) : (
                          <p style={{ color: '#999', fontStyle: 'italic' }}>
                            No sections available
                          </p>
                        )}
                      </div>
                    </Collapse.Panel>
                  </Collapse>
                </div>
              ))
            ) : (
              <p
                style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}
              >
                No progress notes available
              </p>
            )}
          </DemographicsContentDiv>
        </Collapse.Panel>
      </Collapse>

      {/* Submit Button - commented out, auto-persist on selection change instead */}
      {/* <div style={{
        padding: '16px',
        textAlign: 'center',
        borderTop: '1px solid #d9d9d9',
        backgroundColor: '#fafafa'
      }}>
        <Button
          type="primary"
          size="large"
          onClick={persistDataSelection}
          loading={false}
          style={{
            minWidth: '200px',
            fontWeight: '500'
          }}
        >
          Save Selection
        </Button>
      </div> */}

      <style>
        {`
      .cpt-code-item {
        margin-bottom: 10px;
        margin-left: 8px;
        font-size: 16px;
        font-weight: 500;
        padding: 6px 10px;
        border: 1px solid transparent;
        border-radius: 4px;
        transition: all 0.2s ease-in-out;
        cursor: pointer;
      }

      .cpt-code-item:hover {
        border: 1px solid #1890ff;
        background: #f0f8ff;
      }

      .ant-collapse {
        gap: 0 !important;
      }

      .ant-collapse > .ant-collapse-item {
        margin-bottom: 0px !important;
        margin-top: 0px !important;
        border-radius: 0px !important;
        padding: 0px !important;
      }

      .ant-collapse-borderless > .ant-collapse-item {
        border-bottom: 1px solid transparent !important;
      }

      .ant-collapse-borderless > .ant-collapse-item > .ant-collapse-header {
        padding: 10px 15px !important;
        border-radius: 0px !important;
        margin: 0px !important;
        line-height: 1 !important;
        display: flex !important;
        align-items: center !important;
      }

      .ant-collapse > .ant-collapse-item > .ant-collapse-header {
        border-radius: 0px !important;
        margin: 0px !important;
      }

      .ant-collapse-content {
        border-radius: 0px !important;
        margin-top: 0px !important;
      }

      .ant-collapse-content-box {
        padding-top: 0px !important;
        max-height: 70vh !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }

      .ant-collapse-content-box::-webkit-scrollbar {
        display: none !important;
      }

      /* Scroll fade gradient for overflowing panel content */
      .ant-collapse-content-box.scroll-fade-active::after {
        content: '';
        position: sticky;
        bottom: -13px;
        display: block;
        height: 50px;
        background: linear-gradient(to bottom, rgba(240, 240, 240, 0), rgba(240, 240, 240, 1));
        pointer-events: none;
        margin-top: -40px;
      }

      .ant-collapse-content-box.scroll-fade-bottom::after {
        display: none;
      }

      /* Progress Note nested collapse styling */
      .ant-collapse .ant-collapse {
        background: transparent;
      }

      .ant-collapse .ant-collapse > .ant-collapse-item {
        border-bottom: none !important;
      }

      .ant-collapse .ant-collapse > .ant-collapse-item > .ant-collapse-header {
        padding: 12px !important;
        align-items: flex-start !important;
      }

      .ant-collapse .ant-collapse .ant-collapse-content {
        background: #fafafa;
      }

      .ant-collapse .ant-collapse .ant-collapse-content-box {
        padding: 12px 16px !important;
      }

      /* Collapse arrow styling */
      .ant-collapse > .ant-collapse-item > .ant-collapse-header {
        display: flex !important;
        align-items: stretch !important;
        padding: 0px !important;
        position: relative;
      }

      .ant-collapse > .ant-collapse-item > .ant-collapse-header .ant-collapse-arrow {
        color: white !important;
        font-size: 12px !important;
        position: absolute !important;
        right: 15px !important;
        top: 50% !important;
        transform: translateY(-50%) rotate(0deg) !important;
        transition: transform 0.3s ease !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      /* When expanded, rotate arrow down */
      .ant-collapse > .ant-collapse-item.ant-collapse-item-active > .ant-collapse-header .ant-collapse-arrow {
        transform: translateY(-50%) rotate(90deg) !important;
      }

      /* Scrollbar hidden for collapse content */
      .ant-collapse-content-box::-webkit-scrollbar {
        display: none;
      }

      /* Make demographics container scrollable */
      .ant-collapse {
        max-height: 100%;
        overflow-y: auto;
      }

      .ant-collapse::-webkit-scrollbar {
        width: 8px;
      }

      .ant-collapse::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }

      .ant-collapse::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
      }

      .ant-collapse::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    `}
      </style>
    </DemographicsContainer>
  );
};

export default Demographics;
